import React, { useEffect, useState } from "react"
import {
  Form,
  Input,
  Button,
  DatePicker,
  TimePicker,
  Select,
  Radio,
  Row,
  Col,
  Divider,
  Card,
  message,
  Checkbox,
  InputNumber,
  Alert,
} from "antd"
import PanelLayout from "../../../Layout/PanelLayout"
import SelectBox from "../../../components/SelectBox/SelectBox"
import apiClient from "../../../Utils/apiClient"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import "../../Operations/Operations.scss"

dayjs.extend(customParseFormat)

const { TextArea } = Input
const { Option } = Select

const TIMEZONES = [
  "Asia/Kolkata",
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Dubai",
  "Asia/Singapore",
  "Australia/Sydney",
]

const WEEKDAYS = [
  { label: "Sun", value: 0 },
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
]

const toHm = (value, fallback) => {
  if (!value) return fallback
  if (typeof value === "string") return value
  if (typeof value.format === "function") return value.format("HH:mm")
  return fallback
}

const splitDeadline = (hours) => {
  const total = Math.max(0, Number(hours) || 0)
  return {
    deadlineDays: Math.floor(total / 24),
    deadlineExtraHours: total % 24,
  }
}

const joinDeadline = (days, hours) =>
  (Number(days) || 0) * 24 + (Number(hours) || 0)

const SchedulerForm = () => {
  const [form] = Form.useForm()
  const { id } = useParams()
  const location = useLocation()
  const isEditMode = !!id

  const [frequency, setFrequency] = useState("one-time")
  const [targetType, setTargetType] = useState("all")
  const [taskType, setTaskType] = useState("")
  const [customTaskType, setCustomTaskType] = useState("")
  const [patchGroups, setPatchGroups] = useState([])
  const [devices, setDevices] = useState([])
  const [applicationCategory, setApplicationCategory] = useState([])
  const [chocolatyApps, setChocolatyApps] = useState([])
  const [wingetApps, setWingetApps] = useState([])
  const [windowsUpdates, setWindowsUpdates] = useState([])
  const [chocoData, setChocoData] = useState([])
  const [wingetData, setWingetData] = useState([])
  const [enableRelease, setEnableRelease] = useState(false)
  const [occurrences, setOccurrences] = useState([])
  const windowsCategory = Form.useWatch("windowsCategory", form) || []
  const releaseStartDate = Form.useWatch("releaseStartDate", form) || null
  const releaseEndDate = Form.useWatch("releaseEndDate", form) || null
  const mwEnabled = Form.useWatch(["maintenanceWindow", "enabled"], form)
  const watchFrequency = Form.useWatch("frequency", form)
  const watchTimezone = Form.useWatch("timezone", form)
  const watchTime = Form.useWatch("time", form)
  const watchStartDate = Form.useWatch("startDate", form)
  const watchEndDate = Form.useWatch("endDate", form)
  const watchWeeklyDays = Form.useWatch("weeklyDays", form)
  const watchMonthlyWeek = Form.useWatch("monthlyWeek", form)
  const watchMonthlyWeekday = Form.useWatch("monthlyWeekday", form)
  const watchPtOffset = Form.useWatch("patchTuesdayOffsetDays", form)
  const watchPurpose = Form.useWatch("deploymentPurpose", form)
  const navigate = useNavigate()
  const selectedChocoValues = Form.useWatch("chocolatyApps", form) || []
  const selectedWingetValues = Form.useWatch("wingetApps", form) || []
  useEffect(() => {
    if (location.state?.fromConfirmation && location.state?.formData) {
      const savedData = location.state.formData

      form.setFieldsValue({
        schedulerName: savedData.schedulerName,
        description: savedData.description,
        frequency: savedData.frequency,
        statuses: savedData.statuses,
        compliantStatus: savedData.compliantStatus,
        startDate: savedData.startDate ? dayjs(savedData.startDate) : null,
        endDate: savedData.endDate ? dayjs(savedData.endDate) : null,
        time: savedData.clockTime
          ? dayjs(savedData.clockTime, "HH:mm")
          : savedData.time
            ? dayjs(savedData.time)
            : null,
        timezone: savedData.timezone || "Asia/Kolkata",
        weeklyDays: savedData.weeklyDays || [1, 2, 3, 4, 5],
        monthlyWeek: savedData.monthlyWeek || 2,
        monthlyWeekday: savedData.monthlyWeekday ?? 2,
        patchTuesdayOffsetDays: savedData.patchTuesdayOffsetDays || 0,
        deploymentPurpose: savedData.deploymentPurpose || "required",
        ...splitDeadline(savedData.deadlineOffsetHours),
        rebootBehavior: savedData.rebootBehavior || "if-needed",
        wakeBeforeInstall: Boolean(savedData.wakeBeforeInstall),
        wakeGraceMinutes: savedData.wakeGraceMinutes || 5,
        wakeMaxAttempts: savedData.wakeMaxAttempts || 2,
        deviceRole: savedData.deviceRole || "windows-all",
        retryFailed: savedData.retryFailed !== false,
        requireApprovedUpdates: Boolean(savedData.requireApprovedUpdates),
        retryCount: savedData.retryCount || 3,
        downloadFirst: Boolean(savedData.downloadFirst),
        n1Policy: Boolean(savedData.n1Policy),
        maintenanceWindow: {
          enabled: savedData.maintenanceWindow?.enabled || false,
          days: savedData.maintenanceWindow?.days || [1, 2, 3, 4, 5],
          startTime: savedData.maintenanceWindow?.startTime
            ? dayjs(savedData.maintenanceWindow.startTime, "HH:mm")
            : dayjs("22:00", "HH:mm"),
          endTime: savedData.maintenanceWindow?.endTime
            ? dayjs(savedData.maintenanceWindow.endTime, "HH:mm")
            : dayjs("05:00", "HH:mm"),
          overrideAtDeadline:
            savedData.maintenanceWindow?.overrideAtDeadline !== false,
        },
        targetType: savedData.targetType,
        customTasks: savedData?.customTasks,
        taskType: savedData.taskType,
        customTaskType: savedData?.customTaskType,
        scanType: savedData?.scanType,
        patchGroups: savedData.patchGroups,
        devices: savedData.devices,
        applicationCategory: savedData.applicationCategory,
        windowsCategory: savedData.windowsCategory,
        releaseStartDate: savedData.releaseStartDate
          ? dayjs(savedData.releaseStartDate)
          : null,
        releaseEndDate: savedData.releaseEndDate
          ? dayjs(savedData.releaseEndDate)
          : null,
        chocolatyApps: savedData.chocolatyApps?.map(
          (app) => app.value || app.PackageId
        ),
        wingetApps: savedData.wingetApps?.map((app) => app.value || app.Id),
      })

      setFrequency(savedData.frequency)
      setTargetType(savedData.targetType)
      setTaskType(savedData.taskType)
      setCustomTaskType(savedData?.customTaskType)
      setPatchGroups(savedData.patchGroups || [])
      setDevices(savedData.devices || [])
      setApplicationCategory(savedData.applicationCategory || [])
      setEnableRelease(savedData.enableRelease || false)

      if (savedData.chocoData) {
        setChocoData(savedData.chocoData)
      }
      if (savedData.chocolatyApps) {
        setChocolatyApps(savedData.chocolatyApps)
      }
      if (savedData.wingetApps) {
        setWingetApps(savedData.wingetApps)
      }

      if (savedData.chocoData) {
        setChocoData(savedData.chocoData)
      }
      setChocoData(savedData.chocolatyApps)
      if (savedData.wingetData) {
        setWingetData(savedData.wingetData)
      }
      if (savedData.windowsUpdates) {
        setWindowsUpdates(savedData.windowsUpdates)
      }
    }
  }, [location.state, form])

  const buildPayload = (values) => {
    const startDate = values.startDate
    const isRecurring = values.frequency && values.frequency !== "one-time"
    const endDate = !isRecurring
      ? new Date(new Date(startDate).setHours(23, 59, 59, 999))
      : values.endDate

    return {
      ...values,
      schedulerName: values.schedulerName,
      description: values.description,
      frequency: values.frequency,
      timezone: values.timezone || "Asia/Kolkata",
      statuses: values.statuses,
      compliantStatus: values.compliantStatus,
      status: "pending",
      time: values.time?.toISOString?.() || values.time,
      clockTime: toHm(values.time, "00:00"),
      enableRelease: enableRelease,
      windowsCategory: values.windowsCategory,
      startDate: startDate?.toISOString?.() || startDate,
      endDate: endDate?.toISOString?.() || endDate,
      targetType: values.targetType,
      taskType: values.taskType,
      customTaskType: values?.customTaskType,
      customScript:
        values.taskType === "custom-tasks" && values.customTaskType === "custom"
          ? String(values.customScript || "").trim()
          : values.taskType === "custom-tasks"
            ? ""
            : values.customScript,
      customTasks:
        values.taskType === "custom-tasks" && values.customTaskType === "select"
          ? values.customTasks || []
          : values.taskType === "custom-tasks"
            ? []
            : values.customTasks,
      scanType:
        values.taskType === "device-scan"
          ? values.scanType
          : undefined,
      releaseStartDate: values.releaseStartDate,
      releaseEndDate: values.releaseEndDate,
      patchGroups: values.patchGroups || [],
      devices: values.devices || [],
      applicationCategory: values.applicationCategory || [],
      weeklyDays: values.weeklyDays || [1, 2, 3, 4, 5],
      monthlyWeek: values.monthlyWeek || 2,
      monthlyWeekday: values.monthlyWeekday ?? 2,
      patchTuesdayOffsetDays: values.patchTuesdayOffsetDays || 0,
      deploymentPurpose: values.deploymentPurpose || "required",
      deadlineOffsetHours: joinDeadline(
        values.deadlineDays,
        values.deadlineExtraHours
      ),
      rebootBehavior: values.rebootBehavior || "if-needed",
      wakeBeforeInstall: Boolean(values.wakeBeforeInstall),
      wakeGraceMinutes: Number(values.wakeGraceMinutes) || 5,
      wakeMaxAttempts: Number(values.wakeMaxAttempts) || 2,
      deviceRole:
        values.taskType === "windows-updates"
          ? values.deviceRole || "windows-all"
          : undefined,
      retryFailed: values.retryFailed !== false,
      requireApprovedUpdates: Boolean(values.requireApprovedUpdates),
      retryCount: values.retryCount || 3,
      downloadFirst: Boolean(values.downloadFirst),
      n1Policy: Boolean(values.n1Policy),
      excludedHostnames: String(values.excludedHostnamesText || "")
        .split(/[\n,]+/)
        .map((item) => item.trim())
        .filter(Boolean),
      excludedKbs: String(values.excludedKbsText || "")
        .split(/[\s,]+/)
        .map((item) => item.replace(/^[Kk][Bb]/, "").trim())
        .filter(Boolean),
      pilotPatchGroupId: values.pilotPatchGroupId || undefined,
      broadDelayHours: Number(values.broadDelayHours) || 0,
      maintenanceWindow: {
        enabled: Boolean(values.maintenanceWindow?.enabled),
        days: values.maintenanceWindow?.days || [1, 2, 3, 4, 5],
        startTime: toHm(values.maintenanceWindow?.startTime, "22:00"),
        endTime: toHm(values.maintenanceWindow?.endTime, "05:00"),
        overrideAtDeadline:
          values.maintenanceWindow?.overrideAtDeadline !== false,
      },
      chocolatyApps: applicationCategory.includes("chocolaty")
        ? chocolatyApps.map((app) => ({
            label: app.Title || app.PackageId || app.label,
            value: app.PackageId || app.value,
            source: "choco",
          }))
        : [],
      wingetApps: applicationCategory.includes("winget")
        ? wingetApps.map((app) => ({
            label: app.package?.Name || app.Id || app.label,
            value: app.Id || app.value,
            source: "winget",
          }))
        : [],
    }
  }

  const handleFinish = async (values) => {
    const payload = buildPayload(values)

    try {
      const result = isEditMode
        ? await apiClient(`schedule/update/${id}`, "PUT", payload)
        : await apiClient("schedule/create", "POST", payload)

      if (result?.success) {
        message.success(result.message || "Scheduler saved successfully")
        navigate("/schedules")
      } else {
        message.error(result?.error || result?.message || "Failed to save schedule")
      }
    } catch (err) {
      console.error("Scheduler submit failed", err)
      message.error("Something went wrong. Please try again.")
    }
  }

  const handleViewDetails = async () => {
    try {
      const values = await form.validateFields()

      const formData = buildPayload({
        ...values,
        releaseStartDate: values.releaseStartDate?.toISOString(),
        releaseEndDate: values.releaseEndDate?.toISOString(),
      })
      navigate(`/confirmation-update-details/${taskType}`, {
        state: {
          formValues: formData,
          windowsUpdates,
          wingetData,
          chocoData,
          chocolatyApps,
          wingetApps,
          windowsCategory: values.windowsCategory,
          applicationCategory: applicationCategory,
          releaseStartDate: values.releaseStartDate?.toISOString(),
          releaseEndDate: values.releaseEndDate?.toISOString(),
          isEditMode,
          id,
          formData: {
            ...formData,
            chocolatyApps,
            wingetApps,
            chocoData,
            wingetData,
            windowsUpdates,
            enableRelease,
          },
        },
      })
    } catch (error) {
      console.error("Validation failed:", error)
      message.error("Please fill in all required fields before viewing details")
    }
  }

  const getData = async (id) => {
    try {
      const res = await apiClient(`schedule/getbyschedule/${id}`, "GET")
      if (res?.success && res.schedule) {
        const schedule = res.schedule
        const releaseEnabled = schedule?.enableRelease || false
        setEnableRelease(releaseEnabled)
        form.setFieldsValue({
          ...schedule,
          schedulerName: schedule.schedulerName,
          description: schedule.description,
          startDate: dayjs(schedule.startDate),
          endDate: schedule.endDate ? dayjs(schedule.endDate) : null,
          time: schedule.clockTime
            ? dayjs(schedule.clockTime, "HH:mm")
            : dayjs(schedule.time),
          timezone: schedule.timezone || "Asia/Kolkata",
          weeklyDays: schedule.weeklyDays || [1, 2, 3, 4, 5],
          monthlyWeek: schedule.monthlyWeek || 2,
          monthlyWeekday: schedule.monthlyWeekday ?? 2,
          patchTuesdayOffsetDays: schedule.patchTuesdayOffsetDays || 0,
          deploymentPurpose: schedule.deploymentPurpose || "required",
          ...splitDeadline(schedule.deadlineOffsetHours),
          rebootBehavior: schedule.rebootBehavior || "if-needed",
          wakeBeforeInstall: Boolean(schedule.wakeBeforeInstall),
          wakeGraceMinutes: schedule.wakeGraceMinutes || 5,
          wakeMaxAttempts: schedule.wakeMaxAttempts || 2,
          deviceRole: schedule.deviceRole || "windows-all",
          retryFailed: schedule.retryFailed !== false,
          requireApprovedUpdates: Boolean(schedule.requireApprovedUpdates),
          retryCount: schedule.retryCount || 3,
          downloadFirst: Boolean(schedule.downloadFirst),
          n1Policy: Boolean(schedule.n1Policy),
          excludedHostnamesText: (schedule.excludedHostnames || []).join("\n"),
          excludedKbsText: (schedule.excludedKbs || []).join(", "),
          pilotPatchGroupId: schedule.pilotPatchGroupId || undefined,
          broadDelayHours: schedule.broadDelayHours || 0,
          maintenanceWindow: {
            enabled: schedule.maintenanceWindow?.enabled || false,
            days: schedule.maintenanceWindow?.days || [1, 2, 3, 4, 5],
            startTime: schedule.maintenanceWindow?.startTime
              ? dayjs(schedule.maintenanceWindow.startTime, "HH:mm")
              : dayjs("22:00", "HH:mm"),
            endTime: schedule.maintenanceWindow?.endTime
              ? dayjs(schedule.maintenanceWindow.endTime, "HH:mm")
              : dayjs("05:00", "HH:mm"),
            overrideAtDeadline:
              schedule.maintenanceWindow?.overrideAtDeadline !== false,
          },
          frequency: schedule.frequency,
          targetType: schedule.targetType,
          patchGroups: schedule.patchGroups,
          devices: schedule.devices,
          taskType: schedule.taskType,
          customTaskType: schedule?.customTaskType,
          scanType: schedule?.scanType,
          applicationCategory: schedule.applicationCategory || [],
          customTasks: schedule.customTasks || [],
          wingetApps: schedule?.wingetApps?.map((app) => app.value) || [],
          chocolatyApps: schedule?.chocolatyApps?.map((app) => app.value) || [],
          windowsCategory: schedule?.windowsCategory || [],
          releaseStartDate: schedule.releaseStartDate
            ? dayjs(schedule.releaseStartDate)
            : null,
          releaseEndDate: schedule.releaseEndDate
            ? dayjs(schedule.releaseEndDate)
            : null,
        })

        setChocolatyApps(schedule?.chocolatyApps || [])
        setWingetApps(schedule?.wingetApps || [])
        setFrequency(schedule.frequency)
        setTargetType(schedule.targetType)
        setApplicationCategory(schedule.applicationCategory || [])
        setTaskType(schedule.taskType)
        setCustomTaskType(schedule?.customTaskType)
        setPatchGroups(schedule.patchGroups || [])
        setDevices(schedule.devices || [])
      }
    } catch (error) {
      console.error("Error fetching schedule by ID", error)
    }
  }

  useEffect(() => {
    if (isEditMode && !location.state?.fromConfirmation) {
      getData(id)
    }
  }, [id, isEditMode, location.state?.fromConfirmation])

  useEffect(() => {
    if (!watchFrequency || !watchTime || !watchStartDate) {
      setOccurrences([])
      return
    }
    const timer = setTimeout(async () => {
      try {
        const res = await apiClient("schedule/preview", "POST", {
          frequency: watchFrequency,
          timezone: watchTimezone || "Asia/Kolkata",
          clockTime: toHm(watchTime, "00:00"),
          startDate: watchStartDate,
          endDate: watchEndDate || undefined,
          weeklyDays: watchWeeklyDays,
          monthlyWeek: watchMonthlyWeek,
          monthlyWeekday: watchMonthlyWeekday,
          patchTuesdayOffsetDays: watchPtOffset || 0,
        })
        setOccurrences(res?.occurrences || [])
      } catch {
        setOccurrences([])
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [
    watchFrequency,
    watchTimezone,
    watchTime,
    watchStartDate,
    watchEndDate,
    watchWeeklyDays,
    watchMonthlyWeek,
    watchMonthlyWeekday,
    watchPtOffset,
  ])

  useEffect(() => {
    if (!selectedChocoValues.length) return

    const fetchChocoDetails = async () => {
      try {
        const query = {
          name: chocolatyApps?.map((item) => item.label),
          source: "choco",
        }
        const res = await apiClient("devices/get/apps", "POST", query)
        if (res) {
          setChocoData(res)
        }
      } catch (e) {
        console.log("Error fetching choco details", e)
      }
    }

    fetchChocoDetails()
  }, [JSON.stringify(selectedChocoValues)])

  useEffect(() => {
    if (!selectedWingetValues.length) return

    const fetchWingetDetails = async () => {
      try {
        const query = {
          name: wingetApps?.map((item) => item.label),
          source: "winget",
        }
        const res = await apiClient("devices/get/apps", "POST", query)
        if (res) {
          setWingetData(res)
        }
      } catch (e) {
        console.log("Error fetching winget details", e)
      }
    }

    fetchWingetDetails()
  }, [JSON.stringify(selectedWingetValues)])

  const getWindowsUpdates = async () => {
    const params = {
      categories: windowsCategory,
      startDate: releaseStartDate,
      endDate: releaseEndDate,
      page: 1,
      pageSize: 99999,
    }
    try {
      const data = await apiClient(
        `filter/get-windows-update-by-releasedate`,
        "POST",
        params
      )
      if (data) {
        setWindowsUpdates(data.data)
      }
    } catch (err) {
      console.log("err while fetching window update in Schedule Form ", err)
    }
  }

  useEffect(() => {
    if (windowsCategory.length > 0 || releaseStartDate || releaseEndDate) {
      getWindowsUpdates()
    }
  }, [releaseStartDate, releaseEndDate, JSON.stringify(windowsCategory)])

  const handleEnableReleaseChange = (checked) => {
    setEnableRelease(checked)
  }

  const shouldShowViewDetails = () => {
    if (taskType === "windows-updates") {
      if (enableRelease) {
        return releaseStartDate && releaseEndDate
      } else {
        return windowsCategory.length > 0
      }
    } else if (taskType === "app-update") {
      return (
        applicationCategory.length > 0 &&
        (chocolatyApps.length > 0 || wingetApps.length > 0)
      )
    }
    return false
  }

  return (
    <PanelLayout
      title={isEditMode ? "Edit schedule" : "Add schedule"}
      subtitle="Define when and where automated tasks should run."
      actions={
        <>
          <Button onClick={() => navigate("/schedules")}>Cancel</Button>
          {shouldShowViewDetails() ? (
            <Button type="primary" onClick={handleViewDetails}>
              View details
            </Button>
          ) : (
            <Button type="primary" onClick={() => form.submit()}>
              {isEditMode ? "Update schedule" : "Save schedule"}
            </Button>
          )}
        </>
      }
    >
      <Card className="ops-card" bordered={false}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{
            targetType: "all",
            frequency: "one-time",
            timezone: "Asia/Kolkata",
            weeklyDays: [1, 2, 3, 4, 5],
            monthlyWeek: 2,
            monthlyWeekday: 2,
            patchTuesdayOffsetDays: 0,
            deploymentPurpose: "required",
            deadlineDays: 0,
            deadlineExtraHours: 0,
            rebootBehavior: "if-needed",
            wakeBeforeInstall: false,
            wakeGraceMinutes: 5,
            wakeMaxAttempts: 2,
            deviceRole: "windows-all",
            retryFailed: true,
            retryCount: 3,
            downloadFirst: false,
            n1Policy: false,
            broadDelayHours: 0,
            maintenanceWindow: {
              enabled: false,
              days: [1, 2, 3, 4, 5],
              startTime: dayjs("22:00", "HH:mm"),
              endTime: dayjs("05:00", "HH:mm"),
              overrideAtDeadline: true,
            },
          }}
        >
          <Divider>General Info</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="schedulerName"
                label="Scheduler Name"
                rules={[{ required: true, message: "Please enter a name" }]}
              >
                <Input placeholder="e.g. Monthly Security Update" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="description" label="Description">
                <TextArea rows={1} placeholder="Optional description" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Assignment schedule (SCCM)</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="frequency" label="Recurrence">
                <Select onChange={setFrequency}>
                  <Option value="one-time">One-time</Option>
                  <Option value="daily">Daily</Option>
                  <Option value="weekly">Weekly</Option>
                  <Option value="monthly">Monthly (nth weekday)</Option>
                  <Option value="patch-tuesday">
                    Patch Tuesday (2nd Tuesday + offset)
                  </Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="timezone"
                label="Time zone"
                rules={[{ required: true }]}
              >
                <Select showSearch>
                  {TIMEZONES.map((tz) => (
                    <Option key={tz} value={tz}>
                      {tz}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="time"
                label="Software available time"
                extra="When the assignment becomes available to devices."
                rules={[{ required: true }]}
              >
                <TimePicker
                  use12Hours
                  format="h:mm A"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="startDate"
                label="Assignment start"
                extra="No deployments before this date."
                rules={[{ required: true }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  disabledDate={(current) => {
                    return current && current < dayjs().startOf("day")
                  }}
                />
              </Form.Item>
            </Col>
            {frequency !== "one-time" && (
              <Col span={8}>
                <Form.Item
                  name="endDate"
                  label="Assignment end"
                  extra="Leave empty to keep running. The last day still runs until 23:59 in this time zone."
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    disabledDate={(current) => {
                      return current && current < dayjs().startOf("day")
                    }}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
          {occurrences.length > 0 && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message="Next software available times"
              description={occurrences.map((item) => item.label).join(" · ")}
            />
          )}
          {frequency === "weekly" && (
            <Form.Item
              name="weeklyDays"
              label="Run on these days"
              rules={[{ required: true, message: "Select at least one day" }]}
            >
              <Checkbox.Group options={WEEKDAYS} />
            </Form.Item>
          )}
          {frequency === "monthly" && (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="monthlyWeek" label="Week of month">
                  <Select>
                    <Option value={1}>First</Option>
                    <Option value={2}>Second</Option>
                    <Option value={3}>Third</Option>
                    <Option value={4}>Fourth</Option>
                    <Option value={5}>Last</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="monthlyWeekday" label="Weekday">
                  <Select>
                    {WEEKDAYS.map((day) => (
                      <Option key={day.value} value={day.value}>
                        {day.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}
          {frequency === "patch-tuesday" && (
            <Form.Item
              name="patchTuesdayOffsetDays"
              label="Days after Patch Tuesday"
              extra="0 = available on Patch Tuesday. 2 = Thursday after. 7 = one week later. Offsets that land in the next month still fire."
            >
              <InputNumber min={0} max={28} style={{ width: 160 }} />
            </Form.Item>
          )}

          <Divider>Target Devices</Divider>
          <Form.Item name="targetType" label="Target Type">
            <Radio.Group onChange={(e) => setTargetType(e.target.value)}>
              <Radio value="all">All Devices</Radio>
              <Radio value="patch-group">Patch Group</Radio>
              <Radio value="specific">Specific Devices</Radio>
            </Radio.Group>
          </Form.Item>

          {targetType === "patch-group" && (
            <Form.Item
              name="patchGroups"
              label="Select Patch Groups"
              rules={[
                {
                  required: true,
                  message: "Please select at least one patch group",
                },
              ]}
            >
              <SelectBox
                className="w-100 mb-3"
                name="name"
                endPoint="patch-groups"
                placeholder="Search Patch Groups"
                value={patchGroups}
                multiple
                onSelect={(value) => setPatchGroups(value)}
              />
            </Form.Item>
          )}

          {targetType === "specific" && (
            <Form.Item
              name="devices"
              label="Select Devices"
              rules={[
                {
                  required: true,
                  message: "Please select at least one device",
                },
              ]}
            >
              <SelectBox
                className="w-100 mb-3"
                name="hostname"
                endPoint="devices/identifiers"
                placeholder="Search Devices"
                value={devices}
                multiple
                onSelect={(value) => setDevices(value)}
              />
            </Form.Item>
          )}
          {(targetType === "patch-group" || targetType === "all") && (
            <>
              <Form.Item name="statuses" label="Select Statuses">
                <Checkbox.Group
                  options={[
                    { label: "Online", value: "Online" },
                    { label: "Offline", value: "Offline" },
                    { label: "Away", value: "Away" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="compliantStatus"
                label="Select Non-Complient (Optional)"
              >
                <Checkbox.Group
                  options={[
                    { label: "Compliant", value: "Compliant" },
                    { label: "Non Compliant", value: "Non-Compliant" },
                  ]}
                />
              </Form.Item>
            </>
          )}

          <Divider>Task Details</Divider>
          <Form.Item
            name="taskType"
            label="Task Type"
            rules={[{ required: true }]}
          >
            <Select onChange={setTaskType} placeholder="Select task">
              <Option value="app-update">App Update</Option>
              <Option value="windows-updates">Windows Updates</Option>
              <Option value="linux-updates">Linux updates</Option>
              <Option value="macos-updates">macOS updates</Option>
              <Option value="device-scan">Device Scan</Option>
              <Option value="bitlocker-enforce">BitLocker enforce</Option>
              <Option value="custom-tasks">Custom Tasks</Option>
            </Select>
          </Form.Item>

          {taskType === "app-update" && (
            <>
              <Form.Item name="applicationCategory" label="Application Type">
                <Checkbox.Group
                  options={[
                    { label: "Chocolaty", value: "chocolaty" },
                    { label: "Winget", value: "winget" },
                  ]}
                  value={applicationCategory}
                  onChange={setApplicationCategory}
                />
              </Form.Item>

              {applicationCategory.includes("chocolaty") && (
                <Form.Item name="chocolatyApps" label="Select Chocolaty Apps">
                  <SelectBox
                    className="w-100 mb-3"
                    name="name"
                    optionValue="name"
                    endPoint="choco-apps"
                    multiple
                    value={chocolatyApps.map((app) => ({
                      value: app?.value || app?.PackageId,
                      label: app?.label || app?.Title,
                    }))}
                    onChange={(selected, full) => {
                      const fullChocodata = full.filter(
                        (item) => item.users && item.users.length > 0
                      )
                      setChocoData(fullChocodata)

                      setChocolatyApps(
                        selected.map((item) => ({
                          PackageId: item,
                          Title: item,
                          value: item,
                          label: item,
                        }))
                      )
                    }}
                    onFetchSuccess={(fullResult) =>
                      console.log("Full API Result:", fullResult)
                    }
                  />
                </Form.Item>
              )}

              {applicationCategory.includes("winget") && (
                <Form.Item name="wingetApps" label="Select Winget Apps">
                  <SelectBox
                    className="w-100 mb-3"
                    name="name"
                    optionValue="name"
                    endPoint="winget-apps"
                    multiple
                    value={wingetApps.map((app) => ({
                      value: app?.value || app?.Id,
                      label: app?.label || app?.package?.Name || app?.Id,
                    }))}
                    onChange={(selected, full) => {
                      const fullWingetdata = full.filter(
                        (item) => item.users && item.users.length > 0
                      )
                      setWingetData(fullWingetdata)
                      setWingetApps(
                        selected.map((item) => ({
                          Id: item,
                          package: { Name: item },
                          value: item,
                          label: item,
                        }))
                      )
                    }}
                  />
                </Form.Item>
              )}
            </>
          )}

          {taskType === "windows-updates" && (
            <>
              <Form.Item
                name="deviceRole"
                label="Windows collection"
                extra="SCCM-style: deploy to workstations, servers, or both."
              >
                <Radio.Group>
                  <Radio value="windows-all">All Windows</Radio>
                  <Radio value="windows-client">Windows clients</Radio>
                  <Radio value="windows-server">Windows servers</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                name="windowsCategory"
                label="Update classifications"
                extra="Leave empty to install Security and Cumulative (including Windows Server CUs). The agent evaluates what is actually pending on each device."
              >
                <Checkbox.Group
                  options={[
                    { label: "Security", value: "Security Updates" },
                    { label: "Cumulative", value: "Cumulative Updates" },
                    { label: "Feature updates (Win10/11)", value: "Feature Packs" },
                    { label: "Drivers", value: "Drivers" },
                    { label: "Firmware", value: "Firmware" },
                    { label: "Other", value: "Other" },
                  ]}
                />
              </Form.Item>

              <Form.Item label="Enable Release Date Filter">
                <Checkbox
                  checked={enableRelease}
                  onChange={(e) => handleEnableReleaseChange(e.target.checked)}
                >
                  Release Date
                </Checkbox>
              </Form.Item>

              {enableRelease && (
                <>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="releaseStartDate"
                        label="Release Start Date"
                        rules={[
                          {
                            required: enableRelease,
                            message: "Please select release start date",
                          },
                        ]}
                      >
                        <DatePicker style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        name="releaseEndDate"
                        label="Release End Date"
                        dependencies={["releaseStartDate"]}
                        rules={[
                          {
                            required: enableRelease,
                            message: "Please select release end date",
                          },
                        ]}
                      >
                        <DatePicker
                          style={{ width: "100%" }}
                          disabledDate={(current) => {
                            const start = form.getFieldValue("releaseStartDate")
                            return (
                              start && current && current.isBefore(start, "day")
                            )
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
            </>
          )}
          {taskType === "device-scan" && (
            <>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Quick scan checks common malware locations and usually finishes in minutes. Full scan checks the entire disk and can take several hours."
              />
              <Form.Item
                name="scanType"
                label="Scan type"
                rules={[
                  {
                    required: true,
                    message: "Select Quick scan or Full scan",
                  },
                ]}
              >
                <Radio.Group>
                  <Radio value="quick">Quick Scan</Radio>
                  <Radio value="full">Full Scan</Radio>
                </Radio.Group>
              </Form.Item>
            </>
          )}
          {taskType === "custom-tasks" && (
            <>
              <Form.Item
                name="customTaskType"
                label="Select Custom Task Type"
                rules={[
                  {
                    required: true,
                    message: "Select custom or saved script",
                  },
                ]}
              >
                <Radio.Group
                  onChange={(e) => setCustomTaskType(e.target.value)}
                >
                  <Radio value="custom">Custom</Radio>
                  <Radio value="select">Select From Table</Radio>
                </Radio.Group>
              </Form.Item>
            </>
          )}
          {customTaskType === "select" && (
            <>
              <Form.Item
                name="customTasks"
                label="Select Custom Task"
                rules={[
                  {
                    required: true,
                    type: "array",
                    min: 1,
                    message: "Select at least one saved script",
                  },
                ]}
              >
                <SelectBox
                  className="w-100 mb-3"
                  name="title"
                  optionValue="_id"
                  endPoint="scripts"
                  multiple
                />
              </Form.Item>
            </>
          )}
          {customTaskType === "custom" && (
            <>
              <Form.Item
                name="customScript"
                label="Custom Script"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Please enter your script/command",
                  },
                ]}
              >
                <TextArea cols={4} />
              </Form.Item>
            </>
          )}

          {taskType && (
            <>
              <Divider>User experience (SCCM)</Divider>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={
                  watchPurpose === "available"
                    ? "Available: devices see the assignment at software available time. Install is forced only at the deadline."
                    : "Required: install during the first maintenance window after available time. If no window is set, install at available time when the deadline is 0, otherwise wait until the deadline."
                }
              />
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="deploymentPurpose"
                    label="Deployment purpose"
                  >
                    <Select>
                      <Option value="required">Required</Option>
                      <Option value="available">Available</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="deadlineDays"
                    label="Deadline (days after available)"
                    extra="SCCM workstation default is often 7 days."
                  >
                    <InputNumber min={0} max={30} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="deadlineExtraHours"
                    label="plus hours"
                    extra="Added to the days. 0 days and 0 hours = as soon as possible."
                  >
                    <InputNumber min={0} max={23} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                {taskType === "windows-updates" && (
                  <Col span={8}>
                    <Form.Item name="rebootBehavior" label="Device restart">
                      <Select>
                        <Option value="none">Do not restart</Option>
                        <Option value="if-needed">Restart if needed</Option>
                        <Option value="force">
                          Force restart after install
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                <Col span={8}>
                  <Form.Item
                    name="retryFailed"
                    label="Retry failed installs"
                    valuePropName="checked"
                  >
                    <Checkbox>Retry failed devices</Checkbox>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="retryCount" label="Maximum retries">
                    <InputNumber min={1} max={10} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                {taskType === "windows-updates" && (
                  <Col span={8}>
                    <Form.Item
                      name="requireApprovedUpdates"
                      valuePropName="checked"
                      label="Approved KBs only"
                    >
                      <Checkbox>Install only approved catalog KBs</Checkbox>
                    </Form.Item>
                  </Col>
                )}
                {taskType === "windows-updates" && (
                  <Col span={8}>
                    <Form.Item
                      name="downloadFirst"
                      valuePropName="checked"
                      label="Download first"
                    >
                      <Checkbox>
                        Download at available time, install in the window
                      </Checkbox>
                    </Form.Item>
                  </Col>
                )}
                {taskType === "windows-updates" && (
                  <Col span={8}>
                    <Form.Item
                      name="n1Policy"
                      valuePropName="checked"
                      label="N-1 quality"
                      extra="Only updates released before this month's Patch Tuesday (WSUS approve N-1; agent still falls back to MU if WSUS fails)."
                    >
                      <Checkbox>N-1 (previous Patch Tuesday set)</Checkbox>
                    </Form.Item>
                  </Col>
                )}
                <Col span={8}>
                  <Form.Item
                    name="wakeBeforeInstall"
                    valuePropName="checked"
                    label="Wake offline PCs"
                    extra="Send Wake-on-LAN before install, then wait for check-in."
                  >
                    <Checkbox>Wake before install</Checkbox>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="wakeGraceMinutes" label="Wake wait (minutes)">
                    <InputNumber min={1} max={30} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="wakeMaxAttempts" label="Wake attempts">
                    <InputNumber min={1} max={5} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="excludedHostnamesText"
                label="Exclude devices"
                extra="One hostname per line. Those PCs are skipped."
              >
                <TextArea rows={2} placeholder="PC-SKIP-01" />
              </Form.Item>
              {taskType === "windows-updates" && (
                <Form.Item
                  name="excludedKbsText"
                  label="Exclude KBs"
                  extra="Comma-separated KB numbers. Applies when a catalog KB list is used."
                >
                  <Input placeholder="5065426, 1234567" />
                </Form.Item>
              )}
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="pilotPatchGroupId"
                    label="Pilot patch group"
                    extra="Optional ring: install the pilot group first."
                  >
                    <SelectBox
                      className="w-100"
                      name="name"
                      endPoint="patch-groups"
                      placeholder="None (all targets at once)"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="broadDelayHours"
                    label="Hours before the rest"
                    extra="After available time. 168 is one week."
                  >
                    <InputNumber min={0} max={720} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name={["maintenanceWindow", "enabled"]}
                valuePropName="checked"
              >
                <Checkbox>
                  Only install during a maintenance window (after available time)
                </Checkbox>
              </Form.Item>
              {mwEnabled && (
                <>
                  <Form.Item
                    name={["maintenanceWindow", "days"]}
                    label="Maintenance window days"
                  >
                    <Checkbox.Group options={WEEKDAYS} />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        name={["maintenanceWindow", "startTime"]}
                        label="Window start"
                      >
                        <TimePicker format="HH:mm" style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name={["maintenanceWindow", "endTime"]}
                        label="Window end"
                      >
                        <TimePicker format="HH:mm" style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name={["maintenanceWindow", "overrideAtDeadline"]}
                        valuePropName="checked"
                      >
                        <Checkbox>
                          Ignore maintenance windows at deadline
                        </Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
            </>
          )}

        </Form>
      </Card>
    </PanelLayout>
  )
}

export default SchedulerForm
